import pageBuilderStore from "./pageBuilderStore";
import { ICssContent } from "./state-interfaces";
import { IDesignBlock, IContentBlock } from "../types/iblock";
import { 
	IColumnsBlockData, 
	IButtonBlockData, 
	IDividerBlockData, 
	IHeadingBlockData,
	ITextBlockData,
	IImageBlockData,
	IHeroSliderBlockData,
	IVideoBlockData,
	ISocialsBlockData,
	IHtmlBlockData
} from "../types/content-block-data";

class ContentManager {
	
	getJsonContent(): Record<string, any> {
		return pageBuilderStore.pageContent.jsonContent;
	}
	
	getHtmlContent(): string {
		return pageBuilderStore.pageContent.htmlContent;
	}
	
	getDesignContent(): IDesignBlock[] {
		return pageBuilderStore.pageContent.designContent;
	}

	/**
	 * Returns the IContentBlock with the specified blockId.
	 *
	 * @param {string} blockId - The unique ID of the content block to find.
	 * @returns {IContentBlock | undefined} The matching content block, or undefined if not found.
	 *
	 * @example
	 * const block = contentManager.getContentBlockById("1234-5678");
	 * if (block) { ... }
	 */
	getContentBlockById(blockId: string): IContentBlock | undefined {
		function findBlock(blocks: IDesignBlock[]): IContentBlock | undefined {
			for (const blk of blocks) {
				if (blk.contentBlock.contentBlockId === blockId) {
					return blk.contentBlock;
				}
				// If this is a columns block, search each column's children
				if (blk.contentBlock.blockType === "columns") {
					const childrenColumns = (blk.contentBlock.blockData as IColumnsBlockData).children;
					for (const column of childrenColumns) {
						const found = findBlock(column); // Recursively search
						if (found) return found;
					}
				}
			}
			return undefined;
		}
		return findBlock(this.getDesignContent());
	}

	/**
	 * Updates configuration fields for a Columns block.
	 * Accepts a partial config object, updating only the provided fields.
	 *
	 * @param {string} blockId - The Columns block's unique ID.
	 * @param {Partial<IColumnsBlockData>} config - Fields to update (children, columnWidths, or any other).
	 */
	setColumnsConfig(
		blockId: string,
		config: Partial<IColumnsBlockData>
	): void {
		// 1. Find the content block
		const block = this.getContentBlockById(blockId);

		// 2. Ensure block exists and is a Columns block
		if (!block) return;
		const blockData = block.blockData as IColumnsBlockData;
		if (!blockData) return;

		// 3. Update only provided fields (shallow merge)
		Object.entries(config).forEach(([key, value]) => {
			(blockData as any)[key] = value;
		});

		// 4. Optionally: re-serialize, trigger autosave, etc.
		// this.serializeAll();
	}

	/**
	 * Updates configuration fields for a Button block.
	 * Accepts a partial config object, updating only the provided fields.
	 *
	 * @param {string} blockId - The Button block's unique ID.
	 * @param {Partial<IButtonBlockData>} config - Fields to update (width, button label, padding, margins, etc).
	 */
	setButtonConfig(blockId: string, config: Partial<IButtonBlockData>) {
		// 1. Find the content block
		const block = this.getContentBlockById(blockId);
		if (!block || block.blockType !== "button") return;
		const blockData = block.blockData as IButtonBlockData;
		if (!blockData) return;

		// 2. Only update provided fields
		Object.entries(config).forEach(([key, value]) => {
			(blockData as any)[key] = value;
		});
	}

	/**
	 * Updates configuration fields for a Divider block.
	 * Accepts a partial config object, updating only the provided fields.
	 *
	 * @param {string} blockId - The Divider block's unique ID.
	 * @param {Partial<IDividerBlockData>} config - Fields to update (width, height, padding, margin, etc).
	 */
	setDividerConfig(blockId: string, config: Partial<IDividerBlockData>) {
		// 1. Find the content block
		const block = this.getContentBlockById(blockId);
		if (!block || block.blockType !== "divider") return;
		const blockData = block.blockData as IDividerBlockData;
		if (!blockData) return;

		// 2. Only update provided fields
		Object.entries(config).forEach(([key, value]) => {
			(blockData as any)[key] = value;
		});
	}

	/**
	 * Updates configuration fields for a Heading block.
	 * Accepts a partial config object, updating only the provided fields.
	 *
	 * @param {string} blockId - The Heading block's unique ID.
	 * @param {Partial<IHeadingBlockData>} config - Fields to update (headingLevel, font family, font size, etc).
	 */
	setHeadingConfig(blockId: string, config: Partial<IHeadingBlockData>) {
		// 1. Find the content block
		const block = this.getContentBlockById(blockId);
		if (!block || block.blockType !== "heading") return;
		const blockData = block.blockData as IHeadingBlockData;
		if (!blockData) return;

		// 2. Only update provided fields
		Object.entries(config).forEach(([key, value]) => {
			(blockData as any)[key] = value;
		});
	}

	/**
	 * Updates configuration fields for a Text block.
	 * Accepts a partial config object, updating only the provided fields.
	 *
	 * @param {string} blockId - The Text block's unique ID.
	 * @param {Partial<ITextBlockData>} config - Fields to update (font family, font size, etc).
	 */
	setTextConfig(blockId: string, config: Partial<ITextBlockData>) {
		// 1. Find the content block
		const block = this.getContentBlockById(blockId);
		if (!block || block.blockType !== "text") return;
		const blockData = block.blockData as ITextBlockData;
		if (!blockData) return;

		// 2. Only update provided fields
		Object.entries(config).forEach(([key, value]) => {
			(blockData as any)[key] = value;
		});
	}

	/**
	 * Updates configuration fields for a Image block.
	 * Accepts a partial config object, updating only the provided fields.
	 *
	 * @param {string} blockId - The Image block's unique ID.
	 * @param {Partial<IImageBlockData>} config - Fields to update (width, height, border radius, padding, etc).
	 */
	setImageConfig(blockId: string, config: Partial<IImageBlockData>) {
		// 1. Find the content block
		const block = this.getContentBlockById(blockId);
		if (!block || block.blockType !== "image") return;
		const blockData = block.blockData as IImageBlockData;
		if (!blockData) return;

		// 2. Only update provided fields
		Object.entries(config).forEach(([key, value]) => {
			(blockData as any)[key] = value;
		});
	}

	/**
	 * Updates configuration fields for a HeroSlider block.
	 * Accepts a partial config object, updating only the provided fields.
	 *
	 * @param {string} blockId - The HeroSlider block's unique ID.
	 * @param {Partial<IHeroSliderBlockData>} config - Fields to update (width, height, border radius, including heroHeader heroText, cta, etc).
	 */
	setHeroSliderConfig(blockId: string, config: Partial<IHeroSliderBlockData>) {
		// 1. Find the content block
		const block = this.getContentBlockById(blockId);
		if (!block || block.blockType !== "hero_slider") return;
		const blockData = block.blockData as IHeroSliderBlockData;
		if (!blockData) return;

		// 2. Only update provided fields
		Object.entries(config).forEach(([key, value]) => {
			(blockData as any)[key] = value;
		});
	}

	/**
	 * Updates configuration fields for a Video block.
	 * Accepts a partial config object, updating only the provided fields.
	 *
	 * @param {string} blockId - The Video block's unique ID.
	 * @param {Partial<IVideoBlockData>} config - Fields to update (for now, only padding).
	 */
	setVideoConfig(blockId: string, config: Partial<IVideoBlockData>) {
		// 1. Find the content block
		const block = this.getContentBlockById(blockId);
		if (!block || block.blockType !== "video") return;
		const blockData = block.blockData as IVideoBlockData;
		if (!blockData) return;

		// 2. Only update provided fields
		Object.entries(config).forEach(([key, value]) => {
			(blockData as any)[key] = value;
		});
	}

	/**
	 * Updates configuration fields for a Socials block.
	 * Accepts a partial config object, updating only the provided fields.
	 *
	 * @param {string} blockId - The Socials block's unique ID.
	 * @param {Partial<ISocialsBlockData>} config - Fields to update (for now, only icon size and spacing).
	 */
	setSocialsConfig(blockId: string, config: Partial<ISocialsBlockData>) {
		// 1. Find the content block
		const block = this.getContentBlockById(blockId);
		if (!block || block.blockType !== "socials") return;
		const blockData = block.blockData as ISocialsBlockData;
		if (!blockData) return;

		// 2. Only update provided fields
		Object.entries(config).forEach(([key, value]) => {
			(blockData as any)[key] = value;
		});
	}

	/**
	 * Updates configuration fields for a Custom HTML block.
	 * Accepts a partial config object, updating only the provided fields.
	 *
	 * @param {string} blockId - The Custom HTML block's unique ID.
	 * @param {Partial<IHtmlBlockData>} config - Fields to update (for now, only padding).
	 */
	setHtmlConfig(blockId: string, config: Partial<IHtmlBlockData>) {
		// 1. Find the content block
		const block = this.getContentBlockById(blockId);
		if (!block || block.blockType !== "html") return;
		const blockData = block.blockData as IHtmlBlockData;
		if (!blockData) return;

		// 2. Only update provided fields
		Object.entries(config).forEach(([key, value]) => {
			(blockData as any)[key] = value;
		});
	}
	
	/**
	 * Updates the stored HTML content string for the current page.
	 */
	setHtmlContent(html: string) {
		pageBuilderStore.pageContent.htmlContent = html;
	}
	
	/**
	 * Updates the JSON mapping structure of the page blocks.
	 */
	setJsonContent(json: Record<string, any>) {
		pageBuilderStore.pageContent.jsonContent = json;
	}

	/**
	 * Returns the current array of CSS content rules for the page.
	 * Each item represents a selector and its associated CSS properties,
	 * ready for export, API usage, or live preview.
	 *
	 * @returns ICssContent[] The array of CSS rule objects in page content.
	 */
	getCssContent(): ICssContent[] {
		return pageBuilderStore.pageContent.cssContent;
	}
	
	/**
	 * Updates the structured CSS content array for export or injection.
	 */
	setCssContent(css: ICssContent[]) {
		pageBuilderStore.pageContent.cssContent = css;
	}

	/**
	 * This is called during the deserialization process to get the matching styles object.
	 * @param selector The selector value that the Content Block uses.
	 * @returns Returns the styles object for a `cssContent` object.
	 */
	getStylesForSelector(selector: string): Record<string, string> | undefined {
		return pageBuilderStore.pageContent.cssContent.find(c => c.selector === selector)?.styles;
	}

	/**
	 * Adds or updates (upserts) CSS content entries within the provided CSS content array.
	 * For each entry in the `updates` array, this method will update the corresponding entry in `arr`
	 * if a matching selector exists; otherwise, it will add the new CSS content to `arr`.
	 *
	 * @param {ICssContent[]} arr - The original array of CSS content objects to be updated.
	 * @param {ICssContent[]} updates - An array of CSS content objects to insert or update within `arr`.
	 * @returns {ICssContent[]} A new array of CSS content objects with the updates applied.
	 *
	 * @example
	 * // Upsert new and updated CSS blocks into existing array
	 * const updatedCssContent = upsertCssContents(existingCss, newOrUpdatedCss);
	 */
	upsertCssContents(arr: ICssContent[], updates: ICssContent[]) {
		for (const update of updates) {
			const idx = arr.findIndex(c => c.selector === update.selector);
			if (idx !== -1) arr[idx] = update;
			else arr.push(update);
		}
	}

	/**
	 * Removes duplicate CSS content entries from the provided array based on their selector.
	 * Only the first occurrence of each unique selector will be retained in the result.
	 *
	 * @param {ICssContent[]} arr - The array of CSS content objects to deduplicate.
	 * @returns {ICssContent[]} A new array with duplicate selectors removed.
	 *
	 * @example
	 * // Remove duplicates from an array of CSS content
	 * const uniqueCssContent = dedupeCssContent(cssContentArray);
	 */
	dedupeCssContent(arr: ICssContent[]): ICssContent[] {
		const map = new Map<string, ICssContent>();
		arr.forEach(entry => {
			map.set(entry.selector, entry);
		});
		const deduped = Array.from(map.values());
		return deduped;
	}
	
	/**
	 * Meant to replace setBlockContent, and eventually, we will move this into a management class.
	 * @param designContent An array of DesignBlocks.
	 */
	setDesignContent(designContent: IDesignBlock[]) {
		pageBuilderStore.pageContent.designContent = designContent;
	}
	
	/**
	 * Prints a readable snapshot of the current designContent state,
	 * including each design block's key UI/runtime props and the nested contentBlock data.
	 */
	debugDesignContent() {
		console.log("------ DESIGN CONTENT DUMP ------");
		this.getDesignContent().forEach((designBlock, idx) => {
			console.log(`Block #${idx + 1}:`);
			console.log("  designBlockId:", designBlock.designBlockId);
			console.log("  designBlockRef:", designBlock.designBlockRef);
			
			if (designBlock.contentBlock) {
				console.log("    └─ contentBlockId:", designBlock.contentBlock.contentBlockId);
				console.log("    └─ blockType:", designBlock.contentBlock.blockType);
				if (designBlock.contentBlock.blockType === "columns") {
					const colData = designBlock.contentBlock.blockData as IColumnsBlockData;
					colData.children.forEach((col, idx) => {
						if (col.length > 0) {
							console.log("    └─ Column", idx, ":");
							col.forEach((blk) => {
								console.log("        └─ contentBlockId:", blk.designBlockId);
								console.log("        └─ contentBlockRef:", blk.designBlockRef?.current);
								console.log("        └─ blockType:", blk.contentBlock.blockType);
							});
						}
					});
				} else
					console.log("    └─ blockData:", designBlock.contentBlock.blockData);
			} else {
				console.log("    └─ (no contentBlock found)");
			}
			console.log("----------------------------------");
		});
	}

	debugDesignContentSS(blocks?: IDesignBlock[]) {
		const designContent = blocks || this.getDesignContent();
		console.log("------ DESIGN CONTENT DUMP ------");
		designContent.forEach((designBlock, idx) => {
			console.log(`Block #${idx + 1}:`);
			console.log("  designBlockId:", designBlock.designBlockId);
			// Optionally skip refs if this is a snapshot!
			if ('designBlockRef' in designBlock) {
				console.log("  designBlockRef:", designBlock.designBlockRef);
			}

			if (designBlock.contentBlock) {
				console.log("    └─ contentBlockId:", designBlock.contentBlock.contentBlockId);
				console.log("    └─ blockType:", designBlock.contentBlock.blockType);
				if (designBlock.contentBlock.blockType === "columns") {
					const colData = designBlock.contentBlock.blockData as IColumnsBlockData;
					colData.children.forEach((col, idx) => {
						if (col.length > 0) {
							console.log("    └─ Column", idx, ":");
							col.forEach((blk) => {
								console.log("        └─ contentBlockId:", blk.designBlockId);
								if ('designBlockRef' in blk) {
									console.log("        └─ contentBlockRef:", blk.designBlockRef?.current);
								}
								console.log("        └─ blockType:", blk.contentBlock.blockType);
							});
						}
					});
				} else {
					console.log("    └─ blockData:", designBlock.contentBlock.blockData);
				}
			} else {
				console.log("    └─ (no contentBlock found)");
			}
			console.log("----------------------------------");
		});
	}
}

const contentManager = new ContentManager();
export default contentManager;